import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export type KafDomain = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  hue: number;
  spiral_order: number;
};

export type KafLessonMeta = {
  id: string;
  slug: string;
  domain_id: string;
  title: string;
  summary: string | null;
  difficulty: string;
  est_minutes: number;
  order_index: number;
};

export type KafBlock = {
  kind: string;
  text?: string;
  prompt?: string;
  pattern?: string;
  cycles?: number;
  seconds?: number;
  minutes?: number;
  label?: string;
};

export type KafLesson = KafLessonMeta & { body: KafBlock[] };

export const listKafDomains = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("kaf_domains")
    .select("id, slug, name, tagline, description, hue, spiral_order")
    .order("spiral_order");
  if (error) throw new Error(error.message);
  return (data ?? []) as KafDomain[];
});

export const getKafDomain = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = publicClient();
    const [{ data: domain }, { data: lessons }] = await Promise.all([
      sb.from("kaf_domains").select("id, slug, name, tagline, description, hue, spiral_order").eq("slug", data.slug).maybeSingle(),
      sb.from("kaf_lessons").select("id, slug, domain_id, title, summary, difficulty, est_minutes, order_index").order("order_index"),
    ]);
    if (!domain) return null;
    return {
      domain: domain as KafDomain,
      lessons: ((lessons ?? []) as KafLessonMeta[]).filter((l) => l.domain_id === domain.id),
    };
  });

export const getKafLesson = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: lesson, error } = await sb
      .from("kaf_lessons")
      .select("id, slug, domain_id, title, summary, difficulty, est_minutes, order_index, body")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!lesson) return null;
    const { data: domain } = await sb
      .from("kaf_domains")
      .select("id, slug, name, tagline, description, hue, spiral_order")
      .eq("id", lesson.domain_id)
      .maybeSingle();
    return { lesson: lesson as KafLesson, domain: domain as KafDomain | null };
  });

// ---- Authenticated: progress, daily practice, journal, creations ---------
export const markKafLessonProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string; status?: "in_progress" | "completed"; secondsSpent?: number; reflection?: string }) => d)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const payload = {
      user_id: userId,
      lesson_id: data.lessonId,
      status: data.status ?? "in_progress",
      seconds_spent: data.secondsSpent ?? 0,
      reflection: data.reflection ?? null,
      completed_at: data.status === "completed" ? new Date().toISOString() : null,
    };
    const { error } = await supabase
      .from("kaf_progress")
      .upsert(payload, { onConflict: "user_id,lesson_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listKafProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("kaf_progress")
      .select("lesson_id, status, completed_at, seconds_spent")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getKafTodayPractice = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const today = new Date().toISOString().slice(0, 10);
    const existing = await supabase
      .from("kaf_daily_assignments")
      .select("id, practice_id, completed_at, reflection, assigned_date")
      .eq("user_id", userId)
      .eq("assigned_date", today)
      .maybeSingle();
    let practiceId = existing.data?.practice_id as string | undefined;
    let assignment = existing.data;
    if (!practiceId) {
      // deterministic draw: hash user_id + today
      const { data: pool } = await supabase.from("kaf_practices").select("id").order("slug");
      const list = (pool ?? []) as { id: string }[];
      if (!list.length) return null;
      let h = 0;
      const key = `${userId}:${today}`;
      for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
      const pick = list[h % list.length];
      practiceId = pick.id;
      const ins = await supabase
        .from("kaf_daily_assignments")
        .insert({ user_id: userId, assigned_date: today, practice_id: practiceId })
        .select("id, practice_id, completed_at, reflection, assigned_date")
        .single();
      assignment = ins.data;
    }
    const { data: practice } = await supabase
      .from("kaf_practices")
      .select("id, slug, kind, title, prompt, body, duration_minutes")
      .eq("id", practiceId!)
      .maybeSingle();
    return { assignment, practice };
  });

export const completeKafPractice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { assignmentId: string; reflection?: string }) => d)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("kaf_daily_assignments")
      .update({ completed_at: new Date().toISOString(), reflection: data.reflection ?? null })
      .eq("id", data.assignmentId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    // Bump streak
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const cur = await supabase
      .from("kaf_streaks")
      .select("current_streak, longest_streak, last_active_date")
      .eq("user_id", userId)
      .maybeSingle();
    const prev = cur.data;
    const nextCurrent = prev?.last_active_date === today
      ? prev.current_streak
      : prev?.last_active_date === yesterday
        ? (prev.current_streak ?? 0) + 1
        : 1;
    const nextLongest = Math.max(nextCurrent, prev?.longest_streak ?? 0);
    await supabase
      .from("kaf_streaks")
      .upsert({ user_id: userId, current_streak: nextCurrent, longest_streak: nextLongest, last_active_date: today });
    return { ok: true, streak: nextCurrent };
  });

// ---- Journal -------------------------------------------------------------
export const listKafJournal = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("kaf_journal_entries")
      .select("id, entry_date, title, body, mood, tags, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("entry_date", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const saveKafJournalEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id?: string; title?: string; body: string; mood?: string; tags?: string[] }) => d)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.id) {
      const { error } = await supabase
        .from("kaf_journal_entries")
        .update({ title: data.title ?? null, body: data.body, mood: data.mood ?? null, tags: data.tags ?? [] })
        .eq("id", data.id)
        .eq("user_id", userId);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: row, error } = await supabase
      .from("kaf_journal_entries")
      .insert({ user_id: userId, title: data.title ?? null, body: data.body, mood: data.mood ?? null, tags: data.tags ?? [] })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteKafJournalEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("kaf_journal_entries")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---- Creative Studio -----------------------------------------------------
export const listKafCreations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("kaf_creations")
      .select("id, kind, title, body, status, media_urls, updated_at")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const saveKafCreation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id?: string; kind: string; title: string; body?: string; status?: string; mediaUrls?: string[] }) => d)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.id) {
      const { error } = await supabase
        .from("kaf_creations")
        .update({
          kind: data.kind,
          title: data.title,
          body: data.body ?? "",
          status: data.status ?? "draft",
          media_urls: data.mediaUrls ?? [],
        })
        .eq("id", data.id)
        .eq("user_id", userId);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: row, error } = await supabase
      .from("kaf_creations")
      .insert({
        user_id: userId,
        kind: data.kind,
        title: data.title,
        body: data.body ?? "",
        status: data.status ?? "draft",
        media_urls: data.mediaUrls ?? [],
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteKafCreation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("kaf_creations")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getKafStreak = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("kaf_streaks")
      .select("current_streak, longest_streak, last_active_date")
      .eq("user_id", context.userId)
      .maybeSingle();
    return data ?? { current_streak: 0, longest_streak: 0, last_active_date: null };
  });
